import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { LOGO_URL, BRAND_CREAM, BRAND_NAVY } from "../brand";
import { KeyRound, Loader2, ArrowRight, Users } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LANGS = [
  { key: "it", flag: "🇮🇹" },
  { key: "en", flag: "🇬🇧" },
  { key: "es", flag: "🇪🇸" },
];
const UI = {
  it: { start: "Prosegui", loading: "Caricamento…", notFound: "Struttura non trovata", soon: "Check-in non ancora disponibile per questa struttura.", guestsQ: "Quanti ospiti?", guestsHint: "Seleziona il numero di ospiti per il tuo soggiorno.", guest: "ospite", guests: "ospiti", missing: "Configurazione mancante per questo numero di ospiti.", redirecting: "Reindirizzamento…" },
  en: { start: "Continue", loading: "Loading…", notFound: "Property not found", soon: "Check-in is not available yet for this property.", guestsQ: "How many guests?", guestsHint: "Select the number of guests for your stay.", guest: "guest", guests: "guests", missing: "Configuration missing for this number of guests.", redirecting: "Redirecting…" },
  es: { start: "Continuar", loading: "Cargando…", notFound: "Alojamiento no encontrado", soon: "El check-in aún no está disponible para este alojamiento.", guestsQ: "¿Cuántos huéspedes?", guestsHint: "Selecciona el número de huéspedes de tu estancia.", guest: "huésped", guests: "huéspedes", missing: "Falta la configuración para este número de huéspedes.", redirecting: "Redirigiendo…" },
};

const detectLang = () => {
  const saved = localStorage.getItem("udh_lang");
  if (saved && ["it", "en", "es"].includes(saved)) return saved;
  const n = (navigator.language || "it").slice(0, 2);
  return ["it", "en", "es"].includes(n) ? n : "it";
};

export default function CheckinPublic() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const bookingCode = params.get("booking_code") || params.get("prenotazione") || "";
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [lang, setLang] = useState(detectLang());
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND}/api/public/checkin/${code}`)
      .then((r) => { setData(r.data); setStatus("ok"); })
      .catch(() => setStatus("error"));
  }, [code]);

  const changeLang = (l) => { setLang(l); localStorage.setItem("udh_lang", l); };
  const ui = UI[lang];
  const w = data?.welcome?.[lang] || {};
  const options = data?.guest_options || [];
  const anyAvailable = options.some((o) => o.available);
  const selOpt = options.find((o) => o.guests === selected);
  const canProceed = !!(selOpt && selOpt.available);

  const proceed = async () => {
    if (!canProceed || submitting) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND}/api/public/checkin/${code}/start`, {
        guests: selected, booking_code: bookingCode,
      });
      if (r.data?.url) window.location.href = r.data.url;
      else setSubmitting(false);
    } catch (e) {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12" style={{ backgroundColor: BRAND_CREAM }} data-testid="checkin-public-page">
      <div className="w-full max-w-md">
        {/* Lang switch */}
        <div className="mb-8 flex justify-center gap-2" data-testid="checkin-lang-switch">
          {LANGS.map((l) => (
            <button key={l.key} data-testid={`checkin-lang-${l.key}`} onClick={() => changeLang(l.key)}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${lang === l.key ? "text-white" : "text-slate-500 hover:text-slate-800"}`}
              style={lang === l.key ? { backgroundColor: BRAND_NAVY } : {}}>
              {l.flag} {l.key.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <img src={LOGO_URL} alt="Urban Dreams Home" className="h-16 w-auto object-contain" />
          </div>
        </div>

        {status === "loading" && (
          <div className="flex flex-col items-center gap-3 py-10 text-slate-500" data-testid="checkin-loading">
            <Loader2 className="animate-spin" size={28} /> <span>{ui.loading}</span>
          </div>
        )}

        {status === "error" && (
          <div className="rounded-2xl bg-white p-8 text-center shadow-sm" data-testid="checkin-error">
            <p className="text-lg font-semibold text-slate-800">{ui.notFound}</p>
          </div>
        )}

        {status === "ok" && data && (
          <div className="rounded-3xl bg-white p-8 text-center shadow-md" data-testid="checkin-card">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl" style={{ backgroundColor: BRAND_NAVY }}>
              <KeyRound size={26} className="text-white" />
            </div>
            <h1 className="mb-1 font-head text-2xl font-bold" style={{ color: BRAND_NAVY }} data-testid="checkin-apartment-name">
              {data.apartment?.name}
            </h1>
            {data.apartment?.zone && <p className="mb-4 text-sm text-slate-400">{data.apartment.zone}</p>}
            <h2 className="mb-2 text-lg font-semibold text-slate-800">{w.title}</h2>
            <p className="mb-6 text-sm leading-relaxed text-slate-500" data-testid="checkin-welcome-text">{w.text}</p>

            {!anyAvailable ? (
              <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-700" data-testid="checkin-unavailable">{ui.soon}</p>
            ) : (
              <>
                {/* Guest selection */}
                <div className="mb-2 flex items-center justify-center gap-2 text-sm font-semibold text-slate-700" data-testid="checkin-guests-question">
                  <Users size={16} style={{ color: BRAND_NAVY }} /> {ui.guestsQ}
                </div>
                <p className="mb-4 text-xs text-slate-400">{ui.guestsHint}</p>
                <div className="mb-6 grid grid-cols-4 gap-2" data-testid="checkin-guest-options">
                  {options.map((o) => (
                    <button key={o.guests} data-testid={`checkin-guest-${o.guests}`}
                      disabled={!o.available}
                      onClick={() => setSelected(o.guests)}
                      className={`flex flex-col items-center justify-center rounded-xl border py-3 text-sm font-semibold transition-all ${
                        selected === o.guests
                          ? "border-transparent text-white shadow-md"
                          : o.available
                            ? "border-slate-200 text-slate-700 hover:border-slate-400"
                            : "cursor-not-allowed border-slate-100 text-slate-300"
                      }`}
                      style={selected === o.guests ? { backgroundColor: BRAND_NAVY } : {}}>
                      {o.guests}
                    </button>
                  ))}
                </div>
                {selected && !canProceed && (
                  <p className="mb-4 text-xs text-amber-600" data-testid="checkin-missing-config">{ui.missing}</p>
                )}

                <button data-testid="checkin-proceed-btn" onClick={proceed} disabled={!canProceed || submitting}
                  className="group flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-semibold text-white transition-transform enabled:hover:scale-[1.02] enabled:active:scale-95 disabled:opacity-40"
                  style={{ backgroundColor: BRAND_NAVY }}>
                  {submitting ? (<><Loader2 size={20} className="animate-spin" /> {ui.redirecting}</>)
                    : (<>{ui.start}<ArrowRight size={20} className="transition-transform group-hover:translate-x-1" /></>)}
                </button>
              </>
            )}
          </div>
        )}

        <p className="mt-8 text-center text-xs text-slate-400">{data?.company_name || "Urban Dreams Home"}</p>
      </div>
    </div>
  );
}
